import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  fetchAnswer,
  fetchDeskApprove,
  fetchDeskConfirm,
  fetchDeskConsent,
  sendAnswer,
  submitDeskApprove,
  submitDeskConfirm,
  submitDeskConsent,
  TicketLinkGone,
} from '../api/easeedeskDesk'
import { CustomerTicketPage } from '../easeedesk/CustomerTicketPage'
import { Spinner } from '../ui/Spinner'

const PAGE: React.CSSProperties = {
  margin: 0,
  font: '16px/1.55 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
  background: '#f4f6f8',
  color: '#22303f',
  minHeight: '100vh',
}

const CARD: React.CSSProperties = {
  maxWidth: '34rem',
  margin: '12vh auto',
  padding: '2rem',
  background: '#fff',
  borderRadius: 10,
  boxShadow: '0 1px 3px rgba(0,0,0,.12)',
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={PAGE}>
      <div style={CARD}>{children}</div>
    </div>
  )
}

function LinkGone() {
  return (
    <Frame>
      <h1 style={{ margin: '0 0 .75rem', fontSize: '1.35rem' }}>This link is no longer valid</h1>
      <p style={{ margin: 0 }}>Please reply to the ticket email, or ask your service desk to send the link again.</p>
    </Frame>
  )
}

function Thanks({ message }: { message: string }) {
  return (
    <Frame>
      <div className="alert alert-success text-center" style={{ margin: 0 }}>
        {message}
      </div>
    </Frame>
  )
}

export function DeskAnswerPage({ token, answer }: { token: string; answer: string }) {
  const q = useQuery({
    queryKey: ['desk', 'answer', token, answer],
    queryFn: () => fetchAnswer(token, answer),
    retry: false,
  })
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [gone, setGone] = useState(false)

  if (gone) return <LinkGone />
  if (done) return <Thanks message={done} />

  if (q.isLoading) {
    return (
      <Frame>
        <Spinner />
      </Frame>
    )
  }

  if (q.isError) {
    if (q.error instanceof TicketLinkGone) return <LinkGone />
    return (
      <Frame>
        <p>Something went wrong. Please try again.</p>
      </Frame>
    )
  }

  const data = q.data!
  const label = data.labels.confirm ?? 'Confirm'

  async function confirm() {
    if (saving) return
    setSaving(true)
    try {
      const res = await sendAnswer(token, answer)
      setDone(res.message)
    } catch (e) {
      if (e instanceof TicketLinkGone) setGone(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Frame>
      <p style={{ marginTop: 0 }}>{data.question}</p>
      <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void confirm()}>
        {label}
      </button>
    </Frame>
  )
}

function DeskDecisionPage({
  token,
  kind,
  fetchPage,
  submit,
  primaryDecision,
  secondaryDecision,
  primaryLabelKey,
  secondaryLabelKey,
}: {
  token: string
  kind: 'confirm' | 'approve'
  fetchPage: (t: string) => Promise<{ ticket: number; question: string; labels: Record<string, string> }>
  submit: (t: string, d: string) => Promise<{ message: string }>
  primaryDecision: string
  secondaryDecision: string
  primaryLabelKey: string
  secondaryLabelKey: string
}) {
  const q = useQuery({
    queryKey: ['desk', kind, token],
    queryFn: () => fetchPage(token),
    retry: false,
  })
  const [saving, setSaving] = useState<'primary' | 'secondary' | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [gone, setGone] = useState(false)

  if (gone) return <LinkGone />
  if (done) return <Thanks message={done} />

  if (q.isLoading) {
    return (
      <Frame>
        <Spinner />
      </Frame>
    )
  }

  if (q.isError) {
    if (q.error instanceof TicketLinkGone) return <LinkGone />
    return (
      <Frame>
        <p>Something went wrong. Please try again.</p>
      </Frame>
    )
  }

  const data = q.data!
  const primaryLabel = data.labels[primaryLabelKey] ?? (kind === 'confirm' ? 'Yes' : 'Approve')
  const secondaryLabel = data.labels[secondaryLabelKey] ?? (kind === 'confirm' ? 'No' : 'Deny')

  async function act(decision: string, which: 'primary' | 'secondary') {
    if (saving) return
    setSaving(which)
    try {
      const res = await submit(token, decision)
      setDone(res.message)
    } catch (e) {
      if (e instanceof TicketLinkGone) setGone(true)
    } finally {
      setSaving(null)
    }
  }

  return (
    <Frame>
      <p style={{ marginTop: 0 }}>{data.question}</p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" disabled={saving !== null} onClick={() => void act(primaryDecision, 'primary')}>
          {primaryLabel}
        </button>
        <button type="button" className="btn btn-default" disabled={saving !== null} onClick={() => void act(secondaryDecision, 'secondary')}>
          {secondaryLabel}
        </button>
      </div>
    </Frame>
  )
}

export function DeskConfirmPage({ token }: { token: string }) {
  return (
    <DeskDecisionPage
      token={token}
      kind="confirm"
      fetchPage={fetchDeskConfirm}
      submit={(t, d) => submitDeskConfirm(t, d as 'yes' | 'no')}
      primaryDecision="yes"
      secondaryDecision="no"
      primaryLabelKey="yes"
      secondaryLabelKey="no"
    />
  )
}

export function DeskApprovePage({ token }: { token: string }) {
  return (
    <DeskDecisionPage
      token={token}
      kind="approve"
      fetchPage={fetchDeskApprove}
      submit={(t, d) => submitDeskApprove(t, d as 'approve' | 'deny')}
      primaryDecision="approve"
      secondaryDecision="deny"
      primaryLabelKey="approve"
      secondaryLabelKey="deny"
    />
  )
}

export function DeskConsentPage({ token }: { token: string }) {
  const q = useQuery({
    queryKey: ['desk', 'consent', token],
    queryFn: () => fetchDeskConsent(token),
    retry: false,
  })
  const [saving, setSaving] = useState<'agree' | 'decline' | null>(null)
  const [note, setNote] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const [gone, setGone] = useState(false)

  if (gone) return <LinkGone />
  if (done) return <Thanks message={done} />

  if (q.isLoading) {
    return (
      <Frame>
        <Spinner />
      </Frame>
    )
  }

  if (q.isError) {
    if (q.error instanceof TicketLinkGone) return <LinkGone />
    return (
      <Frame>
        <p>Something went wrong. Please try again.</p>
      </Frame>
    )
  }

  const data = q.data!
  const labels = data.labels
  const windowText =
    data.change.window_start !== '' && data.change.window_end !== ''
      ? `${data.change.window_start} – ${data.change.window_end}`
      : labels.consent_window ?? 'the proposed window'

  async function act(answer: 'agree' | 'decline') {
    if (saving) return
    setSaving(answer)
    try {
      const res = await submitDeskConsent(token, answer, answer === 'decline' ? note : '')
      setDone(res.message)
    } catch (e) {
      if (e instanceof TicketLinkGone) setGone(true)
    } finally {
      setSaving(null)
    }
  }

  return (
    <Frame>
      <h1 style={{ margin: '0 0 .75rem', fontSize: '1.25rem' }}>{data.change.title}</h1>
      <p style={{ marginTop: 0 }}>
        {data.lang === 'ro'
          ? `Propunerea de schimbare #${data.change.draft_id} ar rula pe ${data.change.machines} mașini în fereastra: ${windowText}.`
          : `Change proposal #${data.change.draft_id} would run on ${data.change.machines} machine(s) during: ${windowText}.`}
      </p>
      <label style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>
        {labels.consent_note ?? 'Suggestion (optional)'}
        <textarea
          className="form-control"
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ marginTop: 6 }}
        />
      </label>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" disabled={saving !== null} onClick={() => void act('agree')}>
          {labels.consent_agree ?? 'The window works for me'}
        </button>
        <button type="button" className="btn btn-default" disabled={saving !== null} onClick={() => void act('decline')}>
          {labels.consent_decline ?? 'The window does not work'}
        </button>
      </div>
    </Frame>
  )
}

/** Pagina tichetului deschisă cu jetonul `page` al biroului. */
export function DeskTicketPage({ token }: { token: string }) {
  return <CustomerTicketPage token={token} desk />
}
